import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import type {
  BuyflowCapiPurchaseIntentTriggeredProto,
  BuyflowPurchaseTriggeredProto,
  BuyflowViewContentTriggeredProto,
  NoomMarketingPixelEventProto,
  NoomMarketingPixelEventProto_AttributionIdsNestedMessageProto,
  NoomMarketingPixelEventProto_SharedPropertiesProto,
} from "@noom/noom-contracts/noom_contracts/events/buyflow/marketing";
import { sendBeacon } from "@utils/fetch";
import { captureException } from "@utils/error";
import { allowCategories, LibCategories, waitForConsent } from "@utils/consent";
import { getRequestMetadata } from "@utils/meristemContext";
import { trackTask } from "@utils/monitoring/events/tasks";
import getStore from "@utils/redux/store";
import { shouldFireMedPixel } from "@utils/userSegment/features";
import { getURLParams } from "@utils/urlParams";
import { getPixelEventSourceUrl } from "./eventSourceUrl";
import { generateEventId } from "./utils";

const SNAPCHAT_CLICK_ID_STORAGE_KEY = "snapchatClickId";
const APPLOVIN_CLICK_ID_COOKIE = "_axeid";
const MARKETING_PIXEL_CURRENCY = "USD";
const VIEW_CONTENT_VALUE = 2.0;

type MarketingPixelLeaf =
  | Pick<NoomMarketingPixelEventProto, "buyflowViewContentTriggered">
  | Pick<NoomMarketingPixelEventProto, "buyflowCapiPurchaseIntentTriggered">
  | Pick<NoomMarketingPixelEventProto, "buyflowPurchaseTriggered">;

function compactAttribution(
  attribution: NoomMarketingPixelEventProto_AttributionIdsNestedMessageProto
): NoomMarketingPixelEventProto_AttributionIdsNestedMessageProto {
  return Object.fromEntries(
    Object.entries(attribution).filter(([, value]) => Boolean(value))
  ) as NoomMarketingPixelEventProto_AttributionIdsNestedMessageProto;
}

/**
 * Safari ITP can revoke localStorage (it becomes null / throws).
 */
function isLocalStorageAvailable(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage !== null;
  } catch {
    return false;
  }
}

function persistSnapchatClickId(clickId: string | undefined) {
  if (!clickId || !isLocalStorageAvailable()) {
    return;
  }
  try {
    if (clickId === localStorage.getItem(SNAPCHAT_CLICK_ID_STORAGE_KEY)) {
      return;
    }
  } catch {
    return;
  }

  // URL ScCid is the most recent click. After C0004, overwrite any older
  // stored id so later pages without the query param still attribute correctly.
  waitForConsent([LibCategories.targetingCookies]).then(() => {
    if (!isLocalStorageAvailable()) {
      return;
    }
    try {
      localStorage.setItem(SNAPCHAT_CLICK_ID_STORAGE_KEY, clickId);
    } catch {
      // Ignore quota / revoked storage.
    }
  });
}

export function getMarketingPixelAttributionIds(): NoomMarketingPixelEventProto_AttributionIdsNestedMessageProto {
  const urlParams = getURLParams();
  let storedSnapchatClickId: string | undefined;
  if (isLocalStorageAvailable()) {
    try {
      storedSnapchatClickId =
        localStorage.getItem(SNAPCHAT_CLICK_ID_STORAGE_KEY) || undefined;
    } catch {
      storedSnapchatClickId = undefined;
    }
  }

  const urlSnapchatClickId = urlParams.ScCid || urlParams.scCid;
  persistSnapchatClickId(urlSnapchatClickId);

  return compactAttribution({
    applovinClickId: urlParams.aleid || Cookies.get(APPLOVIN_CLICK_ID_COOKIE),
    applovinAttributionToken: urlParams.alart,
    snapchatClickId: urlSnapchatClickId || storedSnapchatClickId,
  });
}

/** Origin only — no path, query, or hash (avoids leaking survey steps like `/survey/sex`). */
export function getMarketingPixelCurrentUrl(): string {
  return getPixelEventSourceUrl();
}

export function createMarketingPixelSharedProperties(
  userId: string
): NoomMarketingPixelEventProto_SharedPropertiesProto {
  return {
    userId,
    currentUrl: getMarketingPixelCurrentUrl(),
    clientIpAddress: getRequestMetadata().userIp,
    clientUserAgent: navigator.userAgent,
    eventTime: new Date().toISOString(),
    isMedPixel: shouldFireMedPixel(),
    isTestMode: false,
  };
}

export function createBuyflowViewContentTriggeredEvent(): BuyflowViewContentTriggeredProto {
  return {
    attribution: getMarketingPixelAttributionIds(),
    currency: MARKETING_PIXEL_CURRENCY,
    contentValue: VIEW_CONTENT_VALUE,
  };
}

export function createBuyflowCapiPurchaseIntentTriggeredEvent({
  email,
  eltv,
  userId,
}: {
  email: string;
  eltv?: number;
  userId: string;
}): BuyflowCapiPurchaseIntentTriggeredProto {
  return {
    attribution: getMarketingPixelAttributionIds(),
    email,
    currency: MARKETING_PIXEL_CURRENCY,
    eltv,
    transactionId: generateEventId(userId),
  };
}

export function createBuyflowPurchaseTriggeredEvent({
  email,
  eltv,
  transactionId,
}: {
  email: string;
  eltv?: number;
  transactionId?: string;
}): BuyflowPurchaseTriggeredProto {
  return {
    attribution: getMarketingPixelAttributionIds(),
    email,
    currency: MARKETING_PIXEL_CURRENCY,
    eltv,
    transactionId,
  };
}

async function sendNoomMarketingPixelEvent(
  taskName: string,
  leaf: MarketingPixelLeaf,
  {
    waitForTargetingConsent = false,
  }: { waitForTargetingConsent?: boolean } = {}
) {
  if (waitForTargetingConsent) {
    await waitForConsent([LibCategories.targetingCookies]);
  } else if (!allowCategories([LibCategories.targetingCookies])) {
    return;
  }

  const task = trackTask(taskName);
  try {
    task.start();
    const userId = getStore().getState().userData.user_id || "";
    await sendBeacon("/pixel/v1/i/dataStreamEventTracking/", {
      sharedProperties: {
        eventTimestamp: new Date(),
        eventUuid: uuidv4(),
      },
      noomMarketingPixelEvent: {
        sharedProperties: createMarketingPixelSharedProperties(userId),
        ...leaf,
      },
    });
    task.success();
  } catch (err) {
    captureException(err);
    task.failed(err);
  }
}

export function sendBuyflowViewContentTriggered() {
  // waitForConsent may never resolve if the user denies targeting cookies, so
  // the monitoring task must not start until after consent.
  return sendNoomMarketingPixelEvent(
    "SendBuyflowViewContentTriggeredEvent",
    { buyflowViewContentTriggered: createBuyflowViewContentTriggeredEvent() },
    { waitForTargetingConsent: true }
  );
}

export function sendBuyflowCapiPurchaseIntentTriggered({
  email,
  eltv,
  userId,
}: {
  email: string;
  eltv?: number;
  userId: string;
}) {
  return sendNoomMarketingPixelEvent(
    "SendBuyflowCapiPurchaseIntentTriggeredEvent",
    {
      buyflowCapiPurchaseIntentTriggered:
        createBuyflowCapiPurchaseIntentTriggeredEvent({
          email,
          eltv,
          userId,
        }),
    }
  );
}

export function sendBuyflowPurchaseTriggered({
  email,
  eltv,
  transactionId,
}: {
  email: string;
  eltv?: number;
  transactionId?: string;
}) {
  return sendNoomMarketingPixelEvent("SendBuyflowPurchaseTriggeredEvent", {
    buyflowPurchaseTriggered: createBuyflowPurchaseTriggeredEvent({
      email,
      eltv,
      transactionId,
    }),
  });
}
